import { useContext } from 'react'
import {
  AccreditedGender,
  AppointmentType,
  FilterItem,
  EstablishmentCategory
} from '@/modules/accredited-establishments/api/types'
import {
  FiltersContext,
  FiltersDispatchContext
} from '@/modules/accredited-establishments/contexts'
import {
  useGetPlansFilter,
  useGetSpecialtiesFilter,
  useSealedSearchParams,
  useSurvey
} from '@/modules/accredited-establishments/hooks'
import { usePathname } from 'next/navigation'
import {
  ButtonType,
  EventName
} from '@/global/utils/analytics-events/types/common'
import { dispatchEvent } from '@/global/utils'
import { ANALYTICS_EVENTS_CONSENT_CATEGORY } from '@/global/utils/analytics-events/constants'
import {
  ADDRESS_PARAMS,
  ADDRESS_SEARCH_PARAMS,
  PROVIDER_NAME_PARAMS,
  CATEGORIES_PARAMS,
  PLAN_PARAMS,
  SPECIALTY_PARAMS,
  SUB_SPECIALTY_PARAMS,
  GENDER_PARAMS,
  APPOINTMENT_TYPE_PARAMS
} from '@/modules/accredited-establishments/constants'
import { ALL_ID } from '@/modules/accredited-establishments/constants/filters'

const useFilters = () => {
  const pathname = usePathname()
  const {
    ready,
    categories,
    specialty,
    subSpecialty,
    gender,
    appointmentType,
    plan,
    address,
    providerName,
    view,
    filterPresentation,
    providerSearchOpen
  } = useContext(FiltersContext)
  const filtersDispatch = useContext(FiltersDispatchContext)
  const { updateSealedSearchParams } = useSealedSearchParams()

  const { openSurvey } = useSurvey('treatment')

  const { data: specialties } = useGetSpecialtiesFilter()
  const { data: plans } = useGetPlansFilter()

  const updatePlan = (plan?: FilterItem) => {
    updateSealedSearchParams({
      pathname,
      newParam: [[PLAN_PARAMS, plan?.value || '']]
    })

    dispatchEvent({
      eventName: EventName.FILTER_CLICKED,
      filterName: 'Planos',
      filterValue: plan?.title || ''
    })

    filtersDispatch({
      type: 'UPDATE_FILTERS',
      payload: { plan }
    })

    openSurvey()
  }

  /** With no argument the filter is dropped, but the category stays. */
  const updateSpecialty = (specialty?: FilterItem) => {
    const updatedCategories: EstablishmentCategory[] = ['SPECIALIST']

    updateSealedSearchParams({
      pathname,
      newParam: [
        [CATEGORIES_PARAMS, updatedCategories.join(',')],
        [SPECIALTY_PARAMS, specialty?.value || ''],
        /* The reducer drops the subspecialty below; the url follows it. */
        [SUB_SPECIALTY_PARAMS, '']
      ]
    })

    dispatchEvent({
      eventName: EventName.FILTER_CLICKED,
      filterName: 'Especialidades',
      filterValue: specialty?.title || ''
    })

    filtersDispatch({
      type: 'UPDATE_FILTERS',
      payload: {
        specialty,
        categories: updatedCategories,
        subSpecialty: undefined
      }
    })

    openSurvey()
  }

  /**
   * Dropping the specialty drops the subspecialty with it, so the chain never
   * holds a subspecialty of a specialty that is no longer selected.
   *
   * The value goes to the url so the selection can be shared. Restoring it on
   * load costs an extra request for the options of the specialty in the url —
   * useInitFilters pays it only when the param is present.
   */
  const updateSubSpecialty = (subSpecialty?: FilterItem) => {
    updateSealedSearchParams({
      pathname,
      newParam: [[SUB_SPECIALTY_PARAMS, subSpecialty?.value || '']]
    })

    dispatchEvent({
      eventName: EventName.FILTER_CLICKED,
      filterName: 'Subespecialidade',
      filterValue: subSpecialty?.title || ''
    })

    filtersDispatch({
      type: 'UPDATE_FILTERS',
      payload: { subSpecialty }
    })

    openSurvey()
  }

  /**
   * Genero e atendimento nao dependem da especialidade nem um do outro: cada um
   * e uma fatia independente da busca, e o servico de rede credenciada aceita
   * qualquer um deles isolado. Sem argumento o filtro cai.
   */
  const updateGender = (gender?: AccreditedGender) => {
    updateSealedSearchParams({
      pathname,
      newParam: [[GENDER_PARAMS, gender || '']]
    })

    dispatchEvent({
      eventName: EventName.FILTER_CLICKED,
      filterName: 'Gênero',
      filterValue: gender || ''
    })

    filtersDispatch({
      type: 'UPDATE_FILTERS',
      payload: { gender }
    })

    openSurvey()
  }

  const updateAppointmentType = (appointmentType?: AppointmentType) => {
    updateSealedSearchParams({
      pathname,
      newParam: [[APPOINTMENT_TYPE_PARAMS, appointmentType || '']]
    })

    dispatchEvent({
      eventName: EventName.FILTER_CLICKED,
      filterName: 'Atendimento',
      filterValue: appointmentType || ''
    })

    filtersDispatch({
      type: 'UPDATE_FILTERS',
      payload: { appointmentType }
    })

    openSurvey()
  }

  const updateCategories = (category: EstablishmentCategory) => {
    const isSelected = categories.includes(category)

    /* Single choice, kept as an array for `?categorias=` and the API contract. */
    const updatedCategories: EstablishmentCategory[] = isSelected
      ? []
      : [category]

    if (!isSelected) {
      dispatchEvent({
        eventName: EventName.FILTER_CLICKED,
        filterName: category,
        filterValue: category
      })
    }

    /*
     * Os quatro filtros de especialista — especialidade, subespecialidade,
     * genero e atendimento — so valem enquanto a categoria SPECIALIST vale.
     */
    const keepsSpecialty = updatedCategories.includes('SPECIALIST')

    updateSealedSearchParams({
      pathname,
      newParam: [
        [CATEGORIES_PARAMS, updatedCategories.join(',')],
        ...(keepsSpecialty
          ? []
          : [
              [SPECIALTY_PARAMS, ''],
              [SUB_SPECIALTY_PARAMS, ''],
              [GENDER_PARAMS, ''],
              [APPOINTMENT_TYPE_PARAMS, '']
            ])
      ] as [string, string][]
    })

    filtersDispatch({
      type: 'UPDATE_FILTERS',
      payload: {
        categories: updatedCategories,
        ...(keepsSpecialty
          ? {}
          : {
              specialty: undefined,
              subSpecialty: undefined,
              gender: undefined,
              appointmentType: undefined
            })
      }
    })

    openSurvey()
  }

  const updateAddress = (address: FilterItem) => {
    if (!address.value) return

    updateSealedSearchParams({
      pathname,
      newParam: [
        [ADDRESS_PARAMS, address.value],
        [ADDRESS_SEARCH_PARAMS, address.title]
      ]
    })

    dispatchEvent(
      {
        eventName: EventName.FILTER_CLICKED,
        filterName: 'Localização',
        filterValue: address.title || ''
      },
      { consentCategory: ANALYTICS_EVENTS_CONSENT_CATEGORY }
    )

    filtersDispatch({
      type: 'UPDATE_ADDRESS',
      payload: { address }
    })

    openSurvey()
  }

  const updateProviderName = (providerName: string) => {
    updateSealedSearchParams({
      pathname,
      newParam: [[PROVIDER_NAME_PARAMS, providerName]]
    })

    filtersDispatch({
      type: 'SET_PROVIDER_NAME',
      payload: providerName
    })
  }

  /*
   * Estado de UI compartilhado: o campo de busca por nome vive no titulo, que
   * remonta ao trocar de visao. Guardado aqui, ele nao colapsa nessa troca.
   */
  const setProviderSearchOpen = (open: boolean) => {
    filtersDispatch({
      type: 'SET_PROVIDER_SEARCH_OPEN',
      payload: open
    })
  }

  const setView = (view: 'list' | 'map') => {
    dispatchEvent({
      eventName: EventName.BUTTON_CLICKED,
      buttonType: ButtonType.NEW_PAGE,
      buttonName: view === 'list' ? 'Visualizar Lista' : 'Visualizar Mapa'
    })

    filtersDispatch({
      type: 'SET_VIEW',
      payload: view
    })

    if (view === 'map') {
      openSurvey()
    }
  }

  const clearFilters = () => {
    /*
     * `updateSealedSearchParams` derives the keys to drop from `newParam`, so an
     * empty list would leave the url untouched. The address params are absent
     * on purpose: CLEAR_FILTERS does not reset the address either.
     */
    updateSealedSearchParams({
      pathname,
      newParam: [
        [CATEGORIES_PARAMS, ''],
        [SPECIALTY_PARAMS, ''],
        [SUB_SPECIALTY_PARAMS, ''],
        [GENDER_PARAMS, ''],
        [APPOINTMENT_TYPE_PARAMS, ''],
        [PLAN_PARAMS, ''],
        [PROVIDER_NAME_PARAMS, '']
      ]
    })

    filtersDispatch({
      type: 'CLEAR_FILTERS'
    })
  }

  const hasFilters =
    categories.length > 0 || !!specialty || (!!plan && plan?.value !== ALL_ID)

  return {
    plans,
    plan,
    specialties,
    specialty,
    subSpecialty,
    gender,
    appointmentType,
    categories,
    address,
    providerName,
    ready,
    hasFilters,
    isMapView: view === 'map',
    isListView: view === 'list',
    isCollapsed: filterPresentation === 'collapsed',
    isProviderSearchOpen: providerSearchOpen,
    setProviderSearchOpen,
    updatePlan,
    updateSpecialty,
    updateSubSpecialty,
    updateGender,
    updateAppointmentType,
    updateCategories,
    updateAddress,
    updateProviderName,
    setView,
    clearFilters
  }
}

export { useFilters }
